/**
 * Permission Guard Component
 * Protects routes based on user permissions
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext'
import type { Permission } from '@/contexts/AuthContext'

interface PermissionGuardProps {
  children: React.ReactNode
  // Required permissions (user needs at least one of these)
  permissions: (Permission | string)[]
  // Where to redirect if permission denied (defaults to first available dashboard)
  fallbackPath?: string
}

/**
 * Guards child routes/components based on user permissions.
 * If user doesn't have required permissions, redirects to fallback or appropriate dashboard.
 * ADMIN and DEVELOPER must be granted permissions explicitly, same as any other role.
 */
export function PermissionGuard({ children, permissions, fallbackPath }: PermissionGuardProps) {
  const { hasAnyPermission, hasPermission } = useAuth()
  const location = useLocation()

  // Check if user has any of the required permissions
  if (permissions.length === 0 || hasAnyPermission(permissions)) {
    return <>{children}</>
  }

  // Permission denied - determine where to redirect
  const redirectTo = fallbackPath || getDefaultRedirect(hasPermission)

  return <Navigate to={redirectTo} state={{ from: location }} replace />
}

/**
 * Determines the default redirect path based on user's permissions
 */
function getDefaultRedirect(hasPermission: (perm: string) => boolean): string {
  // Check which dashboard the user has access to
  if (hasPermission(PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD)) {
    return '/executions'
  }
  if (hasPermission(PERMISSIONS.FINANCE_MONITORING_DASHBOARD)) {
    return '/finance'
  }
  // Fallback to home dashboard
  return '/'
}

/**
 * Higher-order component for permission-based access
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permissions: (Permission | string)[]
) {
  return function PermissionWrappedComponent(props: P) {
    return (
      <PermissionGuard permissions={permissions}>
        <Component {...props} />
      </PermissionGuard>
    )
  }
}

export default PermissionGuard
